import React, { useState, useEffect } from 'react';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import useGlobale from '../components/global';
import useMenuItems from '../components/Menu';
import {
  ProduitServiceStats,
  ProduitServiceFilters,
  ProduitServiceList,
  ProduitServiceDetail
} from '../components/produit-service';
import Swal from 'sweetalert2';
import api from '../components/api';

const ProduitServicePage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user } = useGlobale();
  const { menuItems } = useMenuItems();
  
  const [produits, setProduits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProduit, setSelectedProduit] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  
  const [filters, setFilters] = useState({
    search: '',
    categorie: '',
    is_active: '',
    prix_min: '',
    prix_max: '',
    ordering: '-created_at'
  });

  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchStats();
    fetchProduits();
  }, [filters]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/produit_service/admin/products/stats/');
      setStats(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des stats:', error);
    }
  };

  const fetchProduits = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });

      const response = await api.get(`/api/produit_service/admin/products/?${params}`);
      console.log('Produits récupérés:', response.data);
      setProduits(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des produits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleViewProduit = (produit) => {
    setSelectedProduit(produit);
    setShowDetail(true);
  };

  const handleEditProduit = (produit) => {
    setSelectedProduit(produit);
    setShowDetail(true);
  };

  const handleUpdateProduit = async (id, data) => {
    try {
      await api.patch(`/api/produit_service/admin/products/${id}/`, data);
      await Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: 'Produit/service mis à jour avec succès',
        confirmButtonColor: '#FF6B00',
      });
      fetchProduits();
      fetchStats();
      setShowDetail(false);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Impossible de mettre à jour le produit/service',
        confirmButtonColor: '#FF6B00',
      });
    }
  };

  const handleDeleteProduit = async (id) => {
    const result = await Swal.fire({
      title: 'Confirmer la suppression',
      text: 'Êtes-vous sûr de vouloir supprimer ce produit/service ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#FF6B00',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/api/produit_service/admin/products/${id}/`);
        await Swal.fire({
          icon: 'success',
          title: 'Supprimé',
          text: 'Produit/service supprimé avec succès',
          confirmButtonColor: '#FF6B00',
        });
        fetchProduits();
        fetchStats();
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de supprimer le produit/service',
          confirmButtonColor: '#FF6B00',
        });
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await api.post(`/api/produit_service/admin/products/${id}/toggle-status/`);
      await Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: response.data.message,
        confirmButtonColor: '#FF6B00',
      });
      fetchProduits();
      fetchStats();
      setShowDetail(false);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Impossible de changer le statut',
        confirmButtonColor: '#FF6B00',
      });
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F4F8E9]">
      <Sidebar
        isOpen={isSidebarOpen}
        menuItems={menuItems}
        onClose={() => setIsSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col w-full">
        <Header
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          user={user}
        />

        <main className="flex-1 w-full p-6">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">
              Gestion des Produits & Services
            </h1>
            <p className="text-gray-600 mt-2">
              Gérez tous les produits et services de la plateforme
            </p>
          </div>

          <div className="space-y-6">
            {/* Statistiques */}
            {stats && <ProduitServiceStats stats={stats} />}

            {/* Filtres */}
            <ProduitServiceFilters
              filters={filters}
              onFilterChange={handleFilterChange}
            />

            {/* Liste des produits */}
            <ProduitServiceList
              produits={produits}
              onView={handleViewProduit}
              onEdit={handleEditProduit}
              onDelete={handleDeleteProduit}
              loading={loading}
            />
          </div>

          {/* Modal de détails */}
          {showDetail && selectedProduit && (
            <ProduitServiceDetail
              produit={selectedProduit}
              onClose={() => setShowDetail(false)}
              onUpdate={handleUpdateProduit}
              onToggleStatus={handleToggleStatus}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default ProduitServicePage;